import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, Clock, MapPin, Utensils, Coffee, Wifi, CheckCircle2, Phone, ArrowRight } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
const serviceData = {
  'individualized-care': {
    title: 'Individualised Care',
    icon: Heart,
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/4-760x428.png',
    description: 'The individualized care plan is offered to our residents because everyone is unique.',
    fullContent: ['The individualized care plan is offered to our residents because everyone is unique.', 'It is offered in a private and semi-private room where family and their loved ones feel at home.', 'The Individualized care plan is supported and approved by our team of residents, doctors, nurses, and staff.'],
    features: ['Private & Semi-private rooms', 'Doctor approved plans', '24/7 Nursing support', 'Family involvement']
  },
  'respite-care': {
    title: 'Respite Care',
    icon: Clock,
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/senior-man-using-laptop-while-sitting-on-sofa-KZR6783-scaled.jpg',
    description: 'Short-term relief for primary caregivers for either a day, several days, or weeks.',
    fullContent: ['At Elisabeth’s House, we offer short-term relief for primary caregivers for either a day, several days, or weeks.', 'Our respite care provides a safe and engaging environment for seniors while giving their regular caregivers a necessary break.', 'Residents during respite stays have full access to all our amenities and professional care services.'],
    features: ['Flexible duration', 'Full amenity access', 'Professional supervision', 'Social engagement']
  },
  'trips-and-outing-activities': {
    title: 'Trips and Outing activities',
    icon: MapPin,
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/front-view-of-senior-friends-interacting-with-each-JURYHC9-scaled.jpg',
    description: 'Transportation provided for medical appointments, shopping, and other leisure activities.',
    fullContent: ['At Elisabeth’s House Assisted Living, transportation is provided for medical appointments, shopping, and other leisure activities.', 'Games, physical activities, religious activities, and church services are provided on request.', 'We believe in keeping our residents connected to the community and engaged with the world around them.'],
    features: ['Medical transportation', 'Shopping excursions', 'Religious services', 'Leisure activities']
  },
  'assisting-with-eating': {
    title: 'Assisting with Eating',
    icon: Utensils,
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/03/pic1-760x428.jpg',
    description: 'Homemade, nutritious meals and snacks that meet special dietary restrictions.',
    fullContent: ['At Elisabeth’s House, we offer homemade, nutritious meals and snacks that meet special dietary restrictions.', 'Our dining experience is designed to be both social and dignified, with support provided as needed.', 'We focus on fresh ingredients and flavors that our residents love, ensuring every meal is a highlight of the day.'],
    features: ['Special dietary plans', 'Nutritious snacks', 'Dignified support', 'Homemade quality']
  },
  dressing: {
    title: 'Dressing & Personal Care',
    icon: Coffee,
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/03/pic2-760x428.jpg',
    description: 'Assistance with all activities, which include; bathing, toileting, grooming, and 24/7 on-call nurse.',
    fullContent: ['At Elisabeth’s House, we offer assistance with all activities, which include; bathing, toileting, grooming, and 24/7 on-call nurse.', 'Our staff provides gentle, respectful assistance with daily routines to help residents look and feel their best.', 'We prioritize dignity and privacy in all personal care interactions.'],
    features: ['Grooming assistance', 'Bathing support', '24/7 On-call nurse', 'Toileting help']
  },
  'electronics-and-wifi': {
    title: 'Telephone, cable TV, wifi available',
    icon: Wifi,
    image: 'https://elisabethhouse.net/wp-content/uploads/2020/03/pic5-760x428.jpg',
    description: 'Computer and internet access and other necessary electronics are available.',
    fullContent: ['At Elisabeth’s House, computer and internet access and other necessary electronics are available.', 'We help our residents stay connected with their loved ones through modern technology.', 'High-speed WiFi and premium cable TV are standard in our living spaces.'],
    features: ['High-speed WiFi', 'Premium Cable TV', 'Computer access', 'Tech support']
  }
};
export const ServiceDetails = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const service = serviceData[slug as keyof typeof serviceData];
  if (!service) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-502">
        <div className="text-center" data-id="element-503">
          <h1 className="text-4xl font-serif mb-4" data-id="element-504">Service Not Found</h1>
          <Link to="/services" className="text-warmCoral hover:underline" data-id="element-505">
            Back to Services
          </Link>
        </div>
      </div>;
  }
  const Icon = service.icon;
  return <div className="min-h-screen bg-white" data-id="element-506">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-end overflow-hidden pt-24 pb-16" data-id="element-507">
        <div className="absolute inset-0 z-0" data-id="element-508">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-509" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent" data-id="element-510" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full" data-id="element-511">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-512">
            <Link to="/services" className="inline-flex items-center space-x-2 text-white/60 text-xs uppercase tracking-[0.3em] mb-8 hover:text-warmCoral transition-colors group" data-id="element-513">
              <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-514" />
              <span data-id="element-515">Back to Services</span>
            </Link>

            <div className="flex items-center space-x-4 mb-6" data-id="element-516">
              <div className="bg-warmCoral p-3 rounded-2xl shadow-lg text-white" data-id="element-517">
                <Icon className="w-8 h-8" data-id="element-518" />
              </div>
              <span className="text-white/60 text-sm uppercase tracking-widest font-medium" data-id="element-519">
                Service Detail
              </span>
            </div>

            <h1 className="text-white text-5xl md:text-7xl font-serif leading-tight mb-4" data-id="element-520">
              {service.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 px-4 md:px-8" data-id="element-521">
        <div className="max-w-7xl mx-auto" data-id="element-522">
          <div className="grid lg:grid-cols-12 gap-16" data-id="element-523">
            {/* Main Content */}
            <div className="lg:col-span-8" data-id="element-524">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="prose prose-lg max-w-none" data-id="element-525">
                <h2 className="text-4xl font-serif text-charcoal mb-8" data-id="element-526">
                  How it works
                </h2>
                {service.fullContent.map((para, i) => <p key={i} className="text-charcoal/70 text-xl leading-relaxed mb-8 font-light" data-id="element-527">
                    {para}
                  </p>)}
              </motion.div>

              <div className="mt-16 grid sm:grid-cols-2 gap-6" data-id="element-528">
                {service.features.map((feature, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="flex items-center space-x-4 bg-warmGray/5 p-6 rounded-3xl border border-warmGray/10" data-id="element-529">
                    <div className="bg-lightBlue/20 p-2 rounded-full text-lightBlue" data-id="element-530">
                      <CheckCircle2 className="w-5 h-5" data-id="element-531" />
                    </div>
                    <span className="text-charcoal font-medium" data-id="element-532">{feature}</span>
                  </motion.div>)}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4" data-id="element-533">
              <div className="lg:sticky lg:top-32 space-y-8" data-id="element-534">
                {/* Contact Widget */}
                <div className="bg-charcoal rounded-[40px] p-10 text-white relative overflow-hidden" data-id="element-535">
                  <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-warmCoral/20 rounded-full blur-3xl" data-id="element-536" />
                  <div className="relative z-10" data-id="element-537">
                    <h3 className="text-2xl font-serif mb-6" data-id="element-538">
                      Need this service?
                    </h3>
                    <p className="text-white/60 mb-8 leading-relaxed" data-id="element-539">
                      Our care coordinators are available 24/7 to discuss how we
                      can help your loved one.
                    </p>
                    <div className="space-y-4 mb-10" data-id="element-540">
                      <div className="flex items-center space-x-4" data-id="element-541">
                        <div className="bg-white/10 p-3 rounded-xl" data-id="element-542">
                          <Phone className="w-5 h-5 text-warmCoral" data-id="element-543" />
                        </div>
                        <div data-id="element-544">
                          <div className="text-xs uppercase tracking-widest opacity-40" data-id="element-545">
                            Call Us
                          </div>
                          <div className="text-lg font-medium" data-id="element-546">
                            +1 (240) 513-6200
                          </div>
                        </div>
                      </div>
                    </div>
                    <Link to="/contact" className="w-full bg-warmCoral hover:bg-white hover:text-charcoal text-white py-4 rounded-2xl font-medium transition-all flex items-center justify-center space-x-2" data-id="element-547">
                      <span data-id="element-548">Get Started</span>
                      <ArrowRight className="w-4 h-4" data-id="element-549" />
                    </Link>
                  </div>
                </div>

                {/* Other Services */}
                <div className="bg-warmGray/5 rounded-[40px] p-10 border border-warmGray/10" data-id="element-550">
                  <h3 className="text-2xl font-serif text-charcoal mb-8" data-id="element-551">
                    Other Services
                  </h3>
                  <div className="space-y-4" data-id="element-552">
                    {Object.entries(serviceData).filter(([key]) => key !== slug).slice(0, 3).map(([key, item]) => <Link key={key} to={`/services/${key}`} className="flex items-center justify-between group p-4 rounded-2xl hover:bg-white hover:shadow-lg transition-all border border-transparent hover:border-warmGray/10" data-id="element-553">
                          <span className="text-charcoal/70 group-hover:text-warmCoral transition-colors font-medium" data-id="element-554">
                            {item.title}
                          </span>
                          <ChevronRight className="w-4 h-4 text-charcoal/20 group-hover:text-warmCoral transition-all group-hover:translate-x-1" data-id="element-555" />
                        </Link>)}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>;
};